// Toolpusher & mud man automation (M2_SPEC §11).
//   Toolpusher: supervises ≤ 3 rigs (assignToolpusher). Rigs under him run rig.auto.program (casing points, mud, bits,
//   sliders per section). Without a program he sets WOB/RPM for the lowest cost per foot. Rig-up/skid × (1 − skill/400).
//   Mud man (1925) on the rig: ppg 'auto' = max(known pore) + 0.3, mud −15%, kick chance −30%. Before 1925 'auto' needs a
//   toolpusher and uses his estimate (pore ± 0.5).
//   Rig builder: rig-up −30%. Pumper: rod-part downtime −50% (prodm2.js). Gauger: theft −40% (gaugerTheftMult).
import { eqDef, fieldDef, rigStats, isAvailable } from './defs.js';
import { strataAt, stratumAt } from './geo.js';
import { activeRigCrew, crewWage } from './crew.js';
import { mudDef, mudRange, autoMwTarget, bestMudFor } from './mud.js';
import { clamp, log, notify } from './util.js';
import { runCasingImpl } from './drilling.js';
import { optimizeSliders, drillCtx } from './rotary.js';

export const MAX_PUSHED_RIGS = 3;

export function toolpusherFor(state, rig) {
  if (!rig?.toolpusherId) return null;
  const c = state.crew[rig.toolpusherId];
  if (!c || c.status !== 'active' || c.injuredUntil || !(c.pushes ?? []).includes(rig.uid)) return null;
  return c;
}

export function hasMudMan(state, rig) {
  return !!rig && activeRigCrew(state, rig).some((c) => c.role === 'mud_man');
}

// Rig-up / skid multiplier from the crew (toolpusher skill, rig builders).
export function rigUpMult(state, rig) {
  let m = 1;
  const tp = toolpusherFor(state, rig);
  if (tp) m *= 1 - tp.skill / 400;
  if (activeRigCrew(state, rig).some((c) => c.role === 'rig_builder')) m *= 0.7;
  return m;
}

export function gaugerTheftMult(state) {
  return Object.values(state.crew).some((c) => c.role === 'gauger' && c.status === 'active' && !c.injuredUntil) ? 0.6 : 1;
}

const PUMPER_CACHE = new WeakMap();
export function pumperOnStaff(state) {
  const hit = PUMPER_CACHE.get(state);
  if (hit && hit.day === state.day) return hit.v;
  const v = Object.values(state.crew).some((c) => c.role === 'pumper' && c.status === 'active' && !c.injuredUntil);
  PUMPER_CACHE.set(state, { day: state.day, v });
  return v;
}

// ---- actions ---------------------------------------------------------------------------------
export function assignToolpusherImpl(state, crewId, rigUids = []) {
  const c = state.crew[crewId];
  if (!c || c.status !== 'active') return { ok: false, msg: 'No such crew member.' };
  if (c.role !== 'toolpusher') return { ok: false, msg: `${c.name} is not a toolpusher.` };
  const list = [...new Set((rigUids ?? []).filter(Boolean))];
  if (list.length > MAX_PUSHED_RIGS) return { ok: false, msg: `A toolpusher can supervise at most ${MAX_PUSHED_RIGS} rigs.` };
  for (const uid of list) if (!state.rigs[uid]) return { ok: false, msg: 'No such rig.' };
  for (const uid of c.pushes ?? []) if (state.rigs[uid]?.toolpusherId === c.id) state.rigs[uid].toolpusherId = null;
  for (const uid of list) {
    const r = state.rigs[uid];
    const prev = r.toolpusherId && state.crew[r.toolpusherId];
    if (prev && prev.id !== c.id) prev.pushes = (prev.pushes ?? []).filter((x) => x !== uid);
    r.toolpusherId = c.id;
  }
  c.pushes = list;
  if (c.rigId && state.rigs[c.rigId]) { state.rigs[c.rigId].crewIds = state.rigs[c.rigId].crewIds.filter((x) => x !== c.id); c.rigId = null; }
  return { ok: true, msg: list.length ? `${c.name} now pushes ${list.map((u) => state.rigs[u].name).join(', ')}.` : `${c.name} has no rigs.` };
}

// Normalize/validate a drilling program. Returns { ok, program } or { ok:false, msg }.
export function normalizeProgram(state, program) {
  if (!program) return { ok: true, program: null };
  const secs = Array.isArray(program.sections) ? program.sections : [];
  if (!secs.length) return { ok: false, msg: 'A drilling program needs at least one section.' };
  const out = [];
  let last = 0;
  for (const s of secs) {
    const toDepth = Math.round(Number(s.toDepth));
    if (!(toDepth > last)) return { ok: false, msg: 'Sections must go deeper one after another.' };
    if (s.casingDefId && eqDef(s.casingDefId)?.cat !== 'casing') return { ok: false, msg: `${s.casingDefId} is not casing.` };
    if (s.mudDefId && eqDef(s.mudDefId)?.cat !== 'mud') return { ok: false, msg: `${s.mudDefId} is not mud.` };
    if (s.bitDefId && eqDef(s.bitDefId)?.cat !== 'bit') return { ok: false, msg: `${s.bitDefId} is not a bit.` };
    out.push({
      toDepth, wob: s.wob != null ? clamp(Number(s.wob), 0.5, 1.6) : null, rpm: s.rpm != null ? clamp(Number(s.rpm), 0.5, 1.6) : null,
      mudDefId: s.mudDefId ?? null, ppg: s.ppg === 'auto' ? 'auto' : s.ppg != null ? Number(s.ppg) : null,
      casingDefId: s.casingDefId ?? null, cement: s.cement ?? null, bitDefId: s.bitDefId ?? null,
      tripBelowPct: s.tripBelowPct != null ? clamp(Number(s.tripBelowPct), 0, 60) : null, cased: false,
    });
    last = toDepth;
  }
  const onPay = ['stop', 'core', 'log', 'drillThrough'].includes(program.onPay) ? program.onPay : 'stop';
  return { ok: true, program: { sections: out, onPay, onKick: 'shutIn' } };
}

export function setDrillingProgramImpl(state, rigUid, program) {
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  if (program && !toolpusherFor(state, rig)) return { ok: false, msg: 'Drilling programs need a toolpusher supervising the rig.' };
  const n = normalizeProgram(state, program);
  if (!n.ok) return n;
  rig.auto.program = n.program;
  if (n.program) {
    rig.auto.drillThroughPay = n.program.onPay === 'drillThrough';
    const w = rig.wellId ? state.wells[rig.wellId] : null;
    if (w) w.stopAt = null;
  }
  return { ok: true, msg: n.program ? `${rig.name}: ${n.program.sections.length}-section drilling program loaded.` : `${rig.name}: drilling program cleared.` };
}

// A sensible program for a field: case through the heaving/water sands near the surface, native clay (or bentonite)
// weighted 'auto', case again above any loss zone, stop on pay. ("Spindletop standard": surface casing ~180 ft through
// the heaving sand, native clay 9.6 ppg to the cap rock, stop on pay.)
export function programTemplate(state, fieldId) {
  const fdef = fieldDef(fieldId);
  if (!fdef) return null;
  const strata = fdef.strata ?? [];
  const casing = ['api_casing', 'steel_casing', 'wrought_iron_casing'].map(eqDef).find((d) => d && isAvailable(d, state))?.id ?? 'steel_casing';
  const cement = ['cement_job', 'perkins_cement'].map(eqDef).find((d) => d && isAvailable(d, state))?.id ?? null;
  const mud = ['treated_mud', 'bentonite_barite', 'native_clay'].map(eqDef).find((d) => d && isAvailable(d, state))?.id ?? 'native_clay';
  let pay = Infinity;
  for (const p of fdef.prospects ?? []) for (const r of p.reservoirs ?? []) pay = Math.min(pay, r.top);
  const sections = [];
  const shallow = strata.filter((s) => (s.heaving || s.water || (s.stableDays ?? 60) < 15) && s.bottom < Math.min(pay, 1500));
  if (shallow.length) {
    const d = Math.max(...shallow.map((s) => s.bottom)) + 20;
    sections.push({ toDepth: d, wob: 1.0, rpm: 1.2, mudDefId: mud, ppg: 'auto', casingDefId: casing, cement, tripBelowPct: 15 });
  }
  const loss = strata.find((s) => s.lossZone && s.top > (sections.at(-1)?.toDepth ?? 0) && s.top < pay);
  if (loss) sections.push({ toDepth: Math.max((sections.at(-1)?.toDepth ?? 0) + 30, loss.top - 10), wob: 1.0, rpm: 1.1, mudDefId: mud, ppg: 'auto', casingDefId: casing, cement, tripBelowPct: 15 });
  const td = Number.isFinite(pay) ? Math.round(pay * 1.6 + 200) : 3000;
  sections.push({ toDepth: Math.max((sections.at(-1)?.toDepth ?? 0) + 50, td), wob: 1.0, rpm: 1.0, mudDefId: mud, ppg: 'auto', casingDefId: null, cement: null, tripBelowPct: 15 });
  return { name: `${fdef.name.split(',')[0]} standard`, sections, onPay: 'stop', onKick: 'shutIn' };
}

// ---- daily (called by rotary.js before drilling; also for cable rigs via drilling.js) -------------------
// Returns true when it started a task (casing etc.) so the drilling step stops for the day.
export function applyAutomation(state, well, rig, { atCasingPoint = false } = {}) {
  if (!rig) return false;
  const tp = toolpusherFor(state, rig);
  const prog = tp ? rig.auto?.program : null;
  const rotary = well.rigType === 'rotary';
  if (prog) {
    const i = prog.sections.findIndex((s) => s.toDepth > well.depth + 1e-6 || (s.casingDefId && !sectionCased(well, s)));
    const sec = prog.sections[i] ?? prog.sections[prog.sections.length - 1];
    // casing point reached?
    if (sec.casingDefId && well.depth >= sec.toDepth - 1e-6 && !sectionCased(well, sec)) {
      if (runSectionCasing(state, well, sec)) return true;
    }
    if (sec.wob != null) well.wob = sec.wob;
    if (rotary && sec.rpm != null) well.rpm = sec.rpm;
    if (sec.tripBelowPct != null) rig.auto.tripBelowPct = sec.tripBelowPct;
    if (rotary && sec.mudDefId && well.mud.defId !== sec.mudDefId && isAvailable(eqDef(sec.mudDefId), state)) well.mud.defId = sec.mudDefId;
    if (rotary) well.mud.auto = sec.ppg === 'auto';
    if (rotary && typeof sec.ppg === 'number') well.mud.ppg = clamp(sec.ppg, ...mudRange(mudDef(well)));
    if (sec.bitDefId) preferBit(state, rig, sec.bitDefId);
    // stop at the next casing point (rotary.js honours well.stopAt)
    well.stopAt = sec.casingDefId && !sectionCased(well, sec) ? sec.toDepth : null;
    if (well.target < prog.sections[prog.sections.length - 1].toDepth - 1e-6 && !well._programTargetSet) {
      well.target = Math.min(rigStats(eqDef(rig.defId)).maxDepth, prog.sections[prog.sections.length - 1].toDepth);
      well._programTargetSet = true;
    }
  } else if (tp && rig.auto?.optimize !== false && well.status === 'DRILLING' && rotary && (state.day - (well._optDay ?? -99) >= 5 || well._optStratum !== stratumIdx(state, well))) {
    optimize(state, well, rig);
  }
  if (rotary && well.mud?.auto && (tp || hasMudMan(state, rig))) autoMud(state, well, rig);
  if (atCasingPoint) return false;
  return false;
}

const stratumIdx = (state, well) => stratumAt(strataAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]), well.depth + 0.01).idx;

function sectionCased(well, sec) {
  return sec.cased || (well.casing ?? []).some((c) => c.bottom >= sec.toDepth - 5);
}

function runSectionCasing(state, well, sec) {
  const r = runCasingImpl(state, well, sec.casingDefId, { cement: sec.cement && isAvailable(eqDef(sec.cement), state) ? sec.cement : null });
  if (r?.ok) { sec.cased = true; well.stopAt = null; log(state, `${well.name}: program casing point at ${Math.round(sec.toDepth)} ft.`, 'info', { wellId: well.id }); return true; }
  if (!well._progCaseWarned || state.day - well._progCaseWarned > 30) {
    well._progCaseWarned = state.day;
    notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'PROGRAM', level: 'warn', text: `${well.name}: program casing point at ${Math.round(sec.toDepth)} ft — ${r?.msg ?? 'could not run casing'}` });
  }
  sec.cased = true; // don't loop forever: carry on and let the player decide
  return false;
}

function preferBit(state, rig, bitDefId) {
  const cur = state.items[rig.bitUid];
  if (cur?.defId === bitDefId) return;
  rig._preferBit = bitDefId;
}

function optimize(state, well, rig) {
  if (well.rigType !== 'rotary') return;
  const s = stratumAt(strataAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]), well.depth + 0.01);
  let daily = 0;
  for (const c of activeRigCrew(state, rig)) daily += crewWage(state, c);
  const best = drillCtx(state, well)?.bit ? optimizeSliders(state, well, s, daily) : null;
  well._optDay = state.day;
  well._optStratum = stratumIdx(state, well);
  if (!best) return;
  well.wob = Math.round(best.wob * 100) / 100;
  if (well.rigType === 'rotary' && best.rpm != null) well.rpm = Math.round(best.rpm * 100) / 100;
}

function autoMud(state, well, rig) {
  const withMudMan = hasMudMan(state, rig);
  const target = autoMwTarget(state, well, { withMudMan });
  let def = mudDef(well);
  const [lo, hi] = mudRange(def);
  if (target > hi + 1e-9 || target < lo - 1e-9) {
    const better = bestMudFor(state, target, def?.id);
    if (better) { well.mud.defId = better.id; def = better; }
  }
  const [lo2, hi2] = mudRange(def);
  well.mud.ppg = Math.round(clamp(target, lo2, hi2) * 10) / 10;
}

